/**
 * 設定パネルコンポーネント
 *
 * PC: ポップオーバー形式
 * スマホ: ボトムシート形式
 *
 * タブで「表示」と「プライバシー」を切り替え。
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import FontSettings from './FontSettings';
import LanguageSettings from './LanguageSettings';
import AnalyticsConsentSettings from './AnalyticsConsentSettings';
import './SettingsPanel.css';

type SettingsTab = 'display' | 'privacy';

interface SettingsPanelProps {
  /** パネルが開いているかどうか */
  isOpen: boolean;
  /** パネルを閉じるコールバック */
  onClose: () => void;
  /** トリガー要素の参照（ポップオーバーの位置決めに使用） */
  triggerRef?: React.RefObject<HTMLElement | null>;
}

/**
 * 設定パネルコンポーネント
 */
const SettingsPanel: React.FC<SettingsPanelProps> = ({ isOpen, onClose, triggerRef }) => {
  const lang = useLanguage();
  const panelRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<SettingsTab>('display');

  /**
   * 外部クリックでパネルを閉じる
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // パネル内のクリックは無視
      if (panelRef.current?.contains(target)) {
        return;
      }
      
      // トリガー要素のクリックは無視（トグル用）
      if (triggerRef?.current?.contains(target)) {
        return;
      }
      
      onClose();
    };

    // 少し遅延を入れてイベントリスナーを追加（クリックイベントの伝播を避ける）
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  /**
   * ESCキーでパネルを閉じる
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  /**
   * スクロールロック（スマホのボトムシート用）
   */
  useEffect(() => {
    if (!isOpen) return;

    // スマホ判定（768px以下）
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    // bodyのスクロールを無効化
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // 閉じるボタンのクリックハンドラ
  const handleCloseClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* オーバーレイ（スマホのみ） */}
      <div className="settings-panel__overlay" onClick={onClose} />

      {/* パネル本体 */}
      <div
        ref={panelRef}
        className="settings-panel"
        role="dialog"
        aria-modal="true"
        aria-label={lang === 'ja' ? '設定' : 'Settings'}
      >
        {/* ヘッダー */}
        <div className="settings-panel__header">
          <h3 className="settings-panel__title">
            {lang === 'ja' ? '設定' : 'Settings'}
          </h3>
          <button
            className="settings-panel__close-btn"
            onClick={handleCloseClick}
            aria-label={lang === 'ja' ? '閉じる' : 'Close'}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* タブ */}
        <div className="settings-panel__tabs" role="tablist" aria-label={lang === 'ja' ? '設定カテゴリ' : 'Settings category'}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'display'}
            aria-controls="settings-panel-display"
            id="tab-display"
            className={`settings-panel__tab ${activeTab === 'display' ? 'settings-panel__tab--active' : ''}`}
            onClick={() => setActiveTab('display')}
          >
            {lang === 'ja' ? '表示' : 'Display'}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'privacy'}
            aria-controls="settings-panel-privacy"
            id="tab-privacy"
            className={`settings-panel__tab ${activeTab === 'privacy' ? 'settings-panel__tab--active' : ''}`}
            onClick={() => setActiveTab('privacy')}
          >
            {lang === 'ja' ? 'プライバシー' : 'Privacy'}
          </button>
        </div>

        {/* コンテンツ */}
        <div className="settings-panel__content">
          {activeTab === 'display' && (
            <div id="settings-panel-display" role="tabpanel" aria-labelledby="tab-display">
              <LanguageSettings />
              <FontSettings />
            </div>
          )}
          {activeTab === 'privacy' && (
            <div id="settings-panel-privacy" role="tabpanel" aria-labelledby="tab-privacy">
              <AnalyticsConsentSettings />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SettingsPanel;
